import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadCaptureModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    phone: '',
    requirement: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRequirementChange = (value) => {
    setFormData({
      ...formData,
      requirement: value
    });
  };

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 5 && hour < 12) {
      return 'Good Morning...!';
    } else if (hour >= 12 && hour < 17) {
      return 'Good Afternoon...!';
    } else {
      return 'Good Evening...!';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.city || !formData.phone || !formData.requirement) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Store lead in database
      await axios.post(`${API}/whatsapp-lead`, {
        name: formData.name,
        city: formData.city,
        phone: formData.phone,
        requirement: formData.requirement
      });

      // Construct WhatsApp message
      const greeting = getTimeBasedGreeting();
      const message = `${greeting} My name is ${formData.name} from ${formData.city}. I am interested in ${formData.requirement}.`;
      const encodedMessage = encodeURIComponent(message);
      const whatsappURL = `https://wa.me/918298297475?text=${encodedMessage}`;

      // Open WhatsApp
      window.open(whatsappURL, '_blank');

      // Success notification
      toast.success('Redirecting to WhatsApp...');

      // Reset form and close modal
      setFormData({
        name: '',
        city: '',
        phone: '',
        requirement: ''
      });
      onClose();
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-2xl relative">
        {/* Corner Logo */}
        <div className="absolute top-4 right-12 opacity-20">
          <img 
            src="/crilam-logo-green.png" 
            alt="" 
            className="h-8 w-auto"
          />
        </div>
        
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#004534]">
            Connect with CriLam Decorative
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="name" className="text-gray-700">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter your name"
              value={formData.name}
              onChange={handleChange}
              required
              className="mt-1.5 rounded-xl"
            />
          </div>

          <div>
            <Label htmlFor="city" className="text-gray-700">
              City <span className="text-red-500">*</span>
            </Label>
            <Input
              id="city"
              name="city"
              type="text"
              placeholder="Enter your city"
              value={formData.city}
              onChange={handleChange}
              required
              className="mt-1.5 rounded-xl"
            />
          </div>

          <div>
            <Label htmlFor="phone" className="text-gray-700">
              Phone Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="Enter your phone number"
              value={formData.phone}
              onChange={handleChange}
              required
              className="mt-1.5 rounded-xl"
            />
          </div>

          <div>
            <Label htmlFor="requirement" className="text-gray-700">
              Requirement Type <span className="text-red-500">*</span>
            </Label>
            <Select value={formData.requirement} onValueChange={handleRequirementChange} required>
              <SelectTrigger className="mt-1.5 rounded-xl">
                <SelectValue placeholder="Select requirement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Acrylic Boards">Acrylic Boards</SelectItem>
                <SelectItem value="CNC Designer Doors">CNC Designer Doors</SelectItem>
                <SelectItem value="Wall Panels">Wall Panels</SelectItem>
                <SelectItem value="Mica Doors">Mica Doors</SelectItem>
                <SelectItem value="Veneer Doors">Veneer Doors</SelectItem>
                <SelectItem value="Dealer Enquiry">Dealer Enquiry</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-6 text-base"
          >
            {isSubmitting ? 'Starting Chat...' : 'Start WhatsApp Chat'}
          </Button>
        </form>

        <p className="text-xs text-gray-500 text-center mt-2">
          Your information will be used to provide better assistance
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureModal;
